import type {
  AddByTextResponse,
  AuthResponse,
  CorrectionChange,
  CorrectionPreview,
  CreateProduct,
  CreateSavedMealResponse,
  DaySummary,
  DiaryEntry,
  Goals,
  ProfileInput,
  Product,
  SavedMeal,
  User,
  WeightMeasurement,
} from '@calorie/shared';
import { tg, tzOffsetMinutes } from './telegram.js';
import { isMock, mockApi } from './mock.js';

const BASE = import.meta.env.VITE_API_URL ?? '/api';
const TOKEN_KEY = 'calorie.session';

let token: string | null = null;

/** Сессия переживает перезапуск мини-аппа, но не переживает истечение срока. */
function loadToken(): string | null {
  if (token) return token;
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw) as { token: string; expiresAt: number };
    if (saved.expiresAt * 1000 < Date.now() + 60_000) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    token = saved.token;
    return token;
  } catch {
    return null;
  }
}

function saveToken(value: string, expiresAt: number) {
  token = value;
  try {
    localStorage.setItem(TOKEN_KEY, JSON.stringify({ token: value, expiresAt }));
  } catch {
    // Приватный режим - работаем на токене в памяти
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { auth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  // Только когда тело есть: на DELETE без тела Fastify отвечает 400
  // «Body cannot be empty when content-type is set to application/json»,
  // и удаление выглядело бы как сбой - строка возвращалась бы в список.
  if (init.body !== undefined) {
    headers.set('Content-Type', 'application/json');
  }

  if (init.auth !== false) {
    const current = loadToken() ?? (await login()).token;
    headers.set('Authorization', `Bearer ${current}`);
  }

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  if (res.status === 401 && init.auth !== false) {
    // Токен протух - один раз перелогиниваемся и повторяем запрос
    token = null;
    localStorage.removeItem(TOKEN_KEY);
    const fresh = await login();
    headers.set('Authorization', `Bearer ${fresh.token}`);
    const retry = await fetch(`${BASE}${path}`, { ...init, headers });
    if (!retry.ok) throw await toError(retry);
    return retry.status === 204 ? (undefined as T) : ((await retry.json()) as T);
  }

  if (!res.ok) throw await toError(res);
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

async function toError(res: Response): Promise<ApiError> {
  let message = `Ошибка ${res.status}`;
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) message = body.error;
  } catch {
    // тело не JSON - оставляем общий текст
  }
  return new ApiError(message, res.status);
}

export async function login(): Promise<AuthResponse> {
  const initData = tg?.initData;
  if (!initData) {
    throw new ApiError('Приложение нужно открывать из Telegram', 401);
  }
  const res = await request<AuthResponse>('/auth/telegram', {
    method: 'POST',
    body: JSON.stringify({ initData, tzOffsetMinutes: tzOffsetMinutes() }),
    auth: false,
  });
  saveToken(res.token, res.expiresAt);
  return res;
}

const realApi = {
  login,

  getDay: (date?: string) =>
    request<DaySummary>(
      `/diary/day?tzOffsetMinutes=${tzOffsetMinutes(date)}` +
        (date ? `&date=${date}` : ''),
    ),

  /** Свободный текст: сервер сам разбирает его и считает БЖУ. */
  addByText: (text: string, date?: string, signal?: AbortSignal) =>
    request<AddByTextResponse>('/diary/entries/text', {
      method: 'POST',
      body: JSON.stringify({ text, date, tzOffsetMinutes: tzOffsetMinutes(date) }),
      signal,
    }),

  /** Ищет точную упаковку: сервер сначала проверяет нашу базу, затем OFF. */
  getProductByBarcode: (barcode: string) =>
    request<Product>(`/products/barcode/${encodeURIComponent(barcode)}`),

  /** Сохраняет отсутствующий в Open Food Facts товар в собственной базе. */
  createProduct: (input: CreateProduct) =>
    request<Product>('/products', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Добавляет уже выбранный по штрихкоду продукт без повторного LLM-поиска. */
  addProductEntry: (productId: string, amount: number, unit: 'g' | 'ml' = 'g', date?: string) =>
    request<{ entry: DiaryEntry }>('/diary/entries/product', {
      method: 'POST',
      body: JSON.stringify({
        productId,
        date,
        ...(unit === 'ml' ? { volumeMl: amount } : { grams: amount }),
        tzOffsetMinutes: tzOffsetMinutes(date),
      }),
    }).then((r) => r.entry),

  deleteEntry: (id: string) =>
    request<void>(`/diary/entries/${id}`, { method: 'DELETE' }),

  /** Разбор свободной правки записи: возвращает, что ИИ понял, без сохранения. */
  correctionPreview: (entryId: string, text: string) =>
    request<{ understood: boolean; preview: CorrectionPreview | null }>(
      `/diary/entries/${entryId}/correction`,
      { method: 'POST', body: JSON.stringify({ text }) },
    ),

  /** Применение подтверждённой правки. */
  correctionApply: (entryId: string, changes: CorrectionChange[]) =>
    request<{ entry: DiaryEntry }>(`/diary/entries/${entryId}/correction/apply`, {
      method: 'POST',
      body: JSON.stringify({ changes }),
    }).then((r) => r.entry),

  /* ─── Сохранённые блюда ─── */

  listMeals: () =>
    request<{ meals: SavedMeal[] }>('/meals').then((r) => r.meals),

  /** Разбирает состав через ИИ и запоминает блюдо с БЖУ. */
  createMeal: (title: string, text: string) =>
    request<CreateSavedMealResponse>('/meals', {
      method: 'POST',
      body: JSON.stringify({ title, text }),
    }),

  deleteMeal: (id: string) =>
    request<void>(`/meals/${id}`, { method: 'DELETE' }),

  /** Кладёт готовое блюдо в дневник мгновенно, без ИИ. */
  addMeal: (id: string, date?: string) =>
    request<{ entry: DiaryEntry }>(`/meals/${id}/add`, {
      method: 'POST',
      body: JSON.stringify({ date, tzOffsetMinutes: tzOffsetMinutes(date) }),
    }).then((r) => r.entry),

  getGoals: () =>
    request<{ goals: Goals | null }>('/me/goals').then((r) => r.goals),

  getWeightHistory: () => request<{ measurements: WeightMeasurement[] }>('/me/weight-history').then((r) => r.measurements),

  saveGoals: (input: ProfileInput) =>
    request<Goals>('/me/goals', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  updateProfile: (firstName: string) =>
    request<{ user: User }>('/me/profile', {
      method: 'PATCH',
      body: JSON.stringify({ firstName }),
    }).then((r) => r.user),
};

/**
 * В демо-режиме тот же интерфейс обслуживают локальные заглушки - так UI можно
 * смотреть и править без сервера. В проде isMock всегда false.
 */
export const api = isMock ? mockApi : realApi;
